import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { INVENTORY_MOVEMENTS_COLLECTION } from '@/services/inventoryMovements/constants';
import type {
  InventoryMovement,
  InventoryMovementInput,
  MovementFilters,
} from '@/services/inventoryMovements/types';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type MovementDoc = {
  companyId: string;
  inventoryItemId: string;
  type: InventoryMovement['type'];
  fromLocationId: string | null;
  toLocationId: string | null;
  reservationId: string | null;
  logisticsOrderId: string | null;
  maintenanceTaskId: string | null;
  quantity: number | null;
  notes: string;
  createdBy: string;
  createdAt: Timestamp;
};

function mapMovement(id: string, data: MovementDoc): InventoryMovement {
  return {
    id,
    companyId: data.companyId,
    inventoryItemId: data.inventoryItemId,
    type: data.type,
    fromLocationId: data.fromLocationId ?? null,
    toLocationId: data.toLocationId ?? null,
    reservationId: data.reservationId ?? null,
    logisticsOrderId: data.logisticsOrderId ?? null,
    maintenanceTaskId: data.maintenanceTaskId ?? null,
    quantity: data.quantity ?? null,
    notes: data.notes ?? '',
    createdBy: data.createdBy,
    createdAt: timestampToDate(data.createdAt),
  };
}

export async function listMovementsForCompany(
  companyId: string = appConfig.companyId,
): Promise<InventoryMovement[]> {
  const snap = await getDocs(
    query(
      collection(getFirestoreDb(), INVENTORY_MOVEMENTS_COLLECTION),
      where('companyId', '==', companyId),
    ),
  );
  return snap.docs
    .map((d) => mapMovement(d.id, d.data() as MovementDoc))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export async function listMovementsForItem(
  itemId: string,
): Promise<InventoryMovement[]> {
  const snap = await getDocs(
    query(
      collection(getFirestoreDb(), INVENTORY_MOVEMENTS_COLLECTION),
      where('inventoryItemId', '==', itemId),
    ),
  );
  return snap.docs
    .map((d) => mapMovement(d.id, d.data() as MovementDoc))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export async function listMovementsForLocation(
  locationId: string,
): Promise<InventoryMovement[]> {
  const all = await listMovementsForCompany();
  return all.filter(
    (m) =>
      m.fromLocationId === locationId || m.toLocationId === locationId,
  );
}

export async function getMovement(id: string): Promise<InventoryMovement | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), INVENTORY_MOVEMENTS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapMovement(snap.id, snap.data() as MovementDoc);
}

export function filterMovements(
  movements: InventoryMovement[],
  filters: MovementFilters,
): InventoryMovement[] {
  let out = movements;
  if (filters.type && filters.type !== 'all') {
    out = out.filter((m) => m.type === filters.type);
  }
  if (filters.inventoryItemId) {
    out = out.filter((m) => m.inventoryItemId === filters.inventoryItemId);
  }
  if (filters.locationId) {
    out = out.filter(
      (m) =>
        m.fromLocationId === filters.locationId ||
        m.toLocationId === filters.locationId,
    );
  }
  if (filters.fromDate) {
    const from = new Date(filters.fromDate).getTime();
    out = out.filter((m) => m.createdAt.getTime() >= from);
  }
  if (filters.toDate) {
    const to = new Date(`${filters.toDate}T23:59:59`).getTime();
    out = out.filter((m) => m.createdAt.getTime() <= to);
  }
  if (filters.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    out = out.filter((m) => m.notes.toLowerCase().includes(q));
  }
  return out;
}

export async function insertMovement(
  input: InventoryMovementInput,
  createdBy: string,
): Promise<InventoryMovement> {
  return withFirestoreError(async () => {
    const ref = doc(
      collection(getFirestoreDb(), INVENTORY_MOVEMENTS_COLLECTION),
    );
    const payload = {
      companyId: appConfig.companyId,
      inventoryItemId: input.inventoryItemId,
      type: input.type,
      fromLocationId: input.fromLocationId ?? null,
      toLocationId: input.toLocationId ?? null,
      reservationId: input.reservationId ?? null,
      logisticsOrderId: input.logisticsOrderId ?? null,
      maintenanceTaskId: input.maintenanceTaskId ?? null,
      quantity: input.quantity ?? null,
      notes: input.notes?.trim() ?? '',
      createdBy,
      ...buildTimestampPayload(true),
    };

    await setDoc(ref, payload);

    const saved = await getDoc(ref);
    if (!saved.exists()) throw new Error('No se pudo registrar el movimiento.');
    const result = mapMovement(saved.id, saved.data() as MovementDoc);
    recordEntityWrite({
      entityType: 'inventoryMovement',
      entityId: result.id,
      isCreate: true,
      after: toAuditSnapshot(result),
      metadata: { movementType: result.type },
    });
    return result;
  }, 'inventoryMovement');
}
